/**
 * DepartmentManager — Admin UI to create, edit, and delete dynamic departments.
 * Stores config in Firestore: departments/{deptId}
 *
 * Props: isAdmin, userId, userName, onClose
 */

import { useState, useEffect } from 'react';
import {
  getFirestore, collection, onSnapshot, addDoc, updateDoc,
  deleteDoc, doc, serverTimestamp, setDoc,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { ALL_ROLES as BUILTIN_ROLES } from '../components/AdminPanel/tabs/MemberManagementTab';
import { PORTAL_CARDS } from '../constants/portalCards';

let _db = null;
const DB = () => { if (!_db) { try { _db = getFirestore(getApp()); } catch(_) {} } return _db; };

// ── Celestial Ledger tokens ───────────────────────────────────────────────────
const C = {
  bg:      '#0c112e',
  panel:   '#141936',
  border:  '#2e3351',
  text:    '#dee0ff',
  bright:  '#ffffff',
  dim:     '#64748b',
  accent:  '#89ceff',
  error:   '#f43f5e',
  success: '#10b981',
  mono:    'monospace',
  sans:    "'Inter', system-ui, sans-serif",
};

// Preset colors
const PRESET_COLORS = [
  '#6366f1','#0ea5e9','#10b981','#f59e0b','#f43f5e','#8b5cf6',
  '#ec4899','#14b8a6','#f97316','#84cc16','#06b6d4','#a855f7',
];

// Preset MS icons
const PRESET_ICONS = [
  'science','biotech','experiment','flask','analytics','bar_chart',
  'hub','memory','satellite_alt','rocket_launch','electrical_services',
  'psychology','folder_special','construction','precision_manufacturing',
  'manage_accounts','school','groups','task_alt','assignment',
];

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g,'_').replace(/^_+|_+$/g,'');
}

// Ids a new department must not take: built-in portals are filtered out of
// getDashboardPortals() on collision, and role ids double as portal ids for
// membership — reusing either would hide the new dept or merge it with another.
const RESERVED_IDS = new Set([
  ...PORTAL_CARDS.map(p => p.id),
  ...BUILTIN_ROLES.map(r => r.id),
]);

function uniqueDeptId(base, takenIds) {
  const root = base || `dept_${Date.now()}`;
  const taken = id => RESERVED_IDS.has(id) || takenIds.includes(id);
  if (!taken(root)) return root;
  let n = 2;
  while (taken(`${root}_${n}`)) n++;
  return `${root}_${n}`;
}

// ── Category editor row ───────────────────────────────────────────────────────
function CategoryRow({ cat, onChange, onDelete }) {
  return (
    <div style={{ display:'flex', gap:8, alignItems:'center', padding:'6px 0' }}>
      <input value={cat.label} onChange={e => onChange({ ...cat, label:e.target.value })}
        placeholder="Category name…"
        style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, color:C.text,
          padding:'5px 9px', fontFamily:C.mono, fontSize:11, outline:'none' }} />
      <input value={cat.icon||''} onChange={e => onChange({ ...cat, icon:e.target.value })}
        placeholder="icon"
        style={{ width:40, background:C.bg, border:`1px solid ${C.border}`, color:C.text,
          padding:'5px 6px', fontFamily:C.mono, fontSize:11, textAlign:'center', outline:'none' }} />
      <button onClick={onDelete}
        style={{ background:'none', border:'none', color:C.error, cursor:'pointer', fontSize:16, padding:'0 4px' }}>×</button>
    </div>
  );
}

// ── Department Form ───────────────────────────────────────────────────────────
function DeptForm({ existing, takenIds = [], userId, userName, onSave, onCancel }) {
  const [name,        setName]       = useState(existing?.name || '');
  const [subtitle,    setSubtitle]   = useState(existing?.subtitle || '');
  const [description, setDesc]       = useState(existing?.description || '');
  const [color,       setColor]      = useState(existing?.color || '#6366f1');
  const [msIcon,      setMsIcon]     = useState(existing?.msIcon || 'folder_special');
  const [categories,  setCategories] = useState(existing?.categories || []);
  const [saving,      setSaving]     = useState(false);
  const [colorInput,  setColorInput] = useState(existing?.color || '#6366f1');
  const [restricted,  setRestricted] = useState(existing?.restricted || false);
  const [allowedRoles, setAllowedRoles] = useState(existing?.allowedRoles || []);

  function toggleRole(id) {
    setAllowedRoles(rs => rs.includes(id) ? rs.filter(r => r !== id) : [...rs, id]);
  }

  function addCategory() {
    setCategories(cs => [...cs, { id: `cat_${Date.now()}`, label:'', icon:'📁', color }]);
  }

  async function save() {
    if (saving) return;
    if (!name.trim()) { alert('Department name is required'); return; }
    setSaving(true);
    // setDoc overwrites, so a new dept must never reuse an existing/reserved id
    const id = existing?.id || uniqueDeptId(slugify(name.trim()), takenIds);
    const cats = categories
      .filter(c => c.label.trim())
      .map(c => ({ ...c, id: c.id || slugify(c.label)||`cat_${Date.now()}` }));

    const payload = {
      name: name.trim(),
      subtitle: subtitle.trim(),
      description: description.trim(),
      color,
      msIcon,
      categories: cats,
      restricted,
      allowedRoles: restricted ? allowedRoles : [],
      updatedAt: serverTimestamp(),
    };

    try {
      if (existing) {
        await updateDoc(doc(DB(), 'departments', existing.id), payload);
      } else {
        await setDoc(doc(DB(), 'departments', id), {
          ...payload,
          createdBy: userId,
          createdByName: userName,
          createdAt: serverTimestamp(),
        });
      }
      setSaving(false);
      onSave();
    } catch(e) {
      setSaving(false);
      alert('Error: '+e.message);
    }
  }

  const Sym = ({ n }) => (
    <span className="material-symbols-outlined" style={{ fontSize:18, lineHeight:1, display:'inline-block' }}>{n}</span>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16, padding:'20px 24px' }}>
      {/* Name */}
      <div>
        <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:6 }}>DEPARTMENT NAME *</div>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Experiments, Propulsion, Avionics…"
          style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, color:C.bright,
            padding:'9px 12px', fontFamily:C.sans, fontSize:14, fontWeight:600, outline:'none', boxSizing:'border-box' }} />
      </div>

      {/* Subtitle */}
      <div>
        <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:6 }}>SUBTITLE (short, for nav)</div>
        <input value={subtitle} onChange={e=>setSubtitle(e.target.value)} placeholder="e.g. Results · Analysis · Conclusions"
          style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, color:C.text,
            padding:'7px 11px', fontFamily:C.mono, fontSize:11, outline:'none', boxSizing:'border-box' }} />
      </div>

      {/* Description */}
      <div>
        <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:6 }}>DESCRIPTION</div>
        <textarea value={description} onChange={e=>setDesc(e.target.value)} rows={3}
          placeholder="What kinds of reports go here? What is this department for?"
          style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, color:C.text,
            padding:'7px 11px', fontFamily:C.sans, fontSize:12, resize:'vertical', outline:'none', boxSizing:'border-box', lineHeight:1.5 }} />
      </div>

      {/* Color */}
      <div>
        <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:8 }}>ACCENT COLOR</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:8 }}>
          {PRESET_COLORS.map(pc => (
            <div key={pc} onClick={()=>{ setColor(pc); setColorInput(pc); }}
              style={{ width:28, height:28, borderRadius:'50%', background:pc, cursor:'pointer',
                border: color===pc ? `2px solid #fff` : '2px solid transparent',
                boxShadow: color===pc ? `0 0 0 2px ${pc}` : 'none', flexShrink:0 }} />
          ))}
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <input type="color" value={colorInput} onChange={e=>{ setColorInput(e.target.value); setColor(e.target.value); }}
            style={{ width:36, height:36, border:'none', background:'none', cursor:'pointer', padding:0 }} />
          <input value={colorInput} onChange={e=>{ setColorInput(e.target.value); if(/^#[0-9a-fA-F]{6}$/.test(e.target.value)) setColor(e.target.value); }}
            placeholder="#6366f1"
            style={{ width:90, background:C.bg, border:`1px solid ${C.border}`, color:C.text,
              padding:'6px 9px', fontFamily:C.mono, fontSize:11, outline:'none' }} />
          <div style={{ width:36, height:36, borderRadius:4, background: color }} />
        </div>
      </div>

      {/* Icon */}
      <div>
        <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:8 }}>ICON (Material Symbol)</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
          {PRESET_ICONS.map(ic => (
            <div key={ic} onClick={()=>setMsIcon(ic)}
              style={{ width:36, height:36, border:`1px solid ${msIcon===ic?color:C.border}`,
                background: msIcon===ic?`${color}22`:C.panel, borderRadius:4,
                display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}>
              <Sym n={ic} />
            </div>
          ))}
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <input value={msIcon} onChange={e=>setMsIcon(e.target.value)} placeholder="material symbol name"
            style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, color:C.text,
              padding:'6px 9px', fontFamily:C.mono, fontSize:11, outline:'none' }} />
          <div style={{ background: C.panel, border:`1px solid ${color}`, padding:'6px 10px',
            display:'flex', alignItems:'center', gap:6, color }}>
            <Sym n={msIcon||'folder'} />
            <span style={{ fontFamily:C.mono, fontSize:10 }}>{name||'Preview'}</span>
          </div>
        </div>
      </div>

      {/* Access restriction */}
      <div>
        <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:8 }}>
          ACCESS
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom: restricted ? 10 : 0 }}>
          <input type="checkbox" checked={restricted} onChange={e => setRestricted(e.target.checked)}
            style={{ width:15, height:15, cursor:'pointer', accentColor: color }} />
          <span style={{ fontFamily:C.sans, fontSize:12, color:C.text }}>
            Restrict to specific roles (admins always have full access)
          </span>
        </label>
        {restricted && (
          <div style={{ background:C.bg, border:`1px solid ${C.border}`, padding:'10px 12px' }}>
            {allowedRoles.length === 0 && (
              <div style={{ fontFamily:C.mono, fontSize:9, color:C.error, marginBottom:8 }}>
                Select at least one role — otherwise only admins can access this department.
              </div>
            )}
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {BUILTIN_ROLES.filter(r => r.id !== 'admin').map(r => (
                <label key={r.id} onClick={() => toggleRole(r.id)}
                  style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer',
                    padding:'4px 10px', border:`1px solid ${allowedRoles.includes(r.id) ? r.color : C.border}`,
                    background: allowedRoles.includes(r.id) ? `${r.color}18` : 'transparent' }}>
                  <span style={{ width:8, height:8, borderRadius:'50%', background:r.color, flexShrink:0 }} />
                  <span style={{ fontFamily:C.mono, fontSize:10,
                    color: allowedRoles.includes(r.id) ? r.color : C.dim }}>{r.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Categories */}
      <div>
        <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:8 }}>
          REPORT CATEGORIES
        </div>
        <div style={{ background:C.bg, border:`1px solid ${C.border}`, padding:'8px 12px', marginBottom:8 }}>
          {categories.length === 0 && (
            <div style={{ fontFamily:C.mono, fontSize:10, color:C.dim, padding:'8px 0', textAlign:'center' }}>
              No categories — reports will use "General"
            </div>
          )}
          {categories.map((cat,i) => (
            <CategoryRow key={cat.id||i} cat={cat}
              onChange={u => setCategories(cs => cs.map((c,j)=>j===i?u:c))}
              onDelete={() => setCategories(cs => cs.filter((_,j)=>j!==i))} />
          ))}
        </div>
        <button onClick={addCategory}
          style={{ padding:'6px 14px', background:`${color}18`, border:`1px solid ${color}44`,
            color, fontFamily:C.mono, fontSize:9, cursor:'pointer', letterSpacing:1 }}>
          + ADD CATEGORY
        </button>
      </div>

      {/* Footer */}
      <div style={{ display:'flex', gap:10, justifyContent:'flex-end', borderTop:`1px solid ${C.border}`, paddingTop:16 }}>
        <button onClick={onCancel}
          style={{ padding:'9px 20px', fontFamily:C.mono, fontSize:10, cursor:'pointer',
            background:'transparent', border:`1px solid ${C.border}`, color:C.dim, letterSpacing:1 }}>
          CANCEL
        </button>
        <button onClick={save} disabled={saving}
          style={{ padding:'9px 24px', fontFamily:C.mono, fontSize:10, cursor:'pointer', letterSpacing:1,
            background: color, border:'none', color:'#fff' }}>
          {saving ? 'SAVING…' : (existing ? 'SAVE CHANGES' : 'CREATE DEPARTMENT')}
        </button>
      </div>
    </div>
  );
}

// ── Department list item ──────────────────────────────────────────────────────
function DeptListItem({ dept, onEdit, onDelete, onOpen }) {
  const Sym = ({ n, size=18 }) => (
    <span className="material-symbols-outlined" style={{ fontSize:size, lineHeight:1, display:'inline-block', color: dept.color }}>{n}</span>
  );
  return (
    <div style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 16px',
      border:`1px solid ${C.border}`, background:C.panel, marginBottom:8 }}>
      <div style={{ width:40, height:40, borderRadius:8, background:`${dept.color}22`,
        border:`1px solid ${dept.color}44`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <Sym n={dept.msIcon||'folder_special'} size={22} />
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ fontFamily:C.sans, fontSize:13, fontWeight:700, color:C.bright }}>{dept.name}</div>
          {dept.restricted && (
            <span title={`Restricted to: ${(dept.allowedRoles||[]).join(', ') || 'admin only'}`}
              style={{ fontFamily:C.mono, fontSize:8, color:C.error, border:`1px solid ${C.error}66`,
                background:`${C.error}18`, padding:'1px 6px' }}>🔒 RESTRICTED</span>
          )}
        </div>
        {dept.subtitle && <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, marginTop:2 }}>{dept.subtitle}</div>}
        {(dept.categories||[]).length > 0 && (
          <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:4 }}>
            {dept.categories.map(c => (
              <span key={c.id} style={{ fontFamily:C.mono, fontSize:8, color:`${dept.color}cc`,
                background:`${dept.color}18`, padding:'1px 6px', borderRadius:2 }}>{c.label}</span>
            ))}
          </div>
        )}
      </div>
      <div style={{ display:'flex', gap:6, flexShrink:0 }}>
        <button onClick={() => onOpen(dept)}
          style={{ padding:'6px 12px', fontFamily:C.mono, fontSize:9, cursor:'pointer',
            background:`${dept.color}22`, border:`1px solid ${dept.color}`, color: dept.color, letterSpacing:1 }}>
          OPEN →
        </button>
        <button onClick={() => onEdit(dept)}
          style={{ padding:'6px 12px', fontFamily:C.mono, fontSize:9, cursor:'pointer',
            background:'transparent', border:`1px solid ${C.border}`, color:C.dim, letterSpacing:1 }}>
          EDIT
        </button>
        <button onClick={() => onDelete(dept)}
          style={{ padding:'6px 12px', fontFamily:C.mono, fontSize:9, cursor:'pointer',
            background:'transparent', border:`1px solid ${C.error}44`, color:C.error, letterSpacing:1 }}>
          DEL
        </button>
      </div>
    </div>
  );
}

// ── Main DepartmentManager ────────────────────────────────────────────────────
export default function DepartmentManager({ isAdmin, userId, userName, initialEditing = null, onClose }) {
  const [depts,   setDepts]   = useState([]);
  const [editing, setEditing] = useState(initialEditing);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (initialEditing) setEditing(initialEditing);
  }, [initialEditing]);

  useEffect(() => {
    const unsub = onSnapshot(collection(DB(), 'departments'), snap => {
      setDepts(snap.docs.map(d => ({ id:d.id, ...d.data() })).sort((a,b)=>a.name?.localeCompare(b.name)));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  async function deleteDept(dept) {
    if (!window.confirm(`Delete department "${dept.name}"? All reports in this department will remain in Firestore but the department will no longer be accessible.`)) return;
    await deleteDoc(doc(DB(), 'departments', dept.id));
  }

  function openDept(dept) {
    window.open(`/dept/${dept.id}`, '_blank');
  }

  if (!isAdmin) return null;

  return (
    <div style={{ position:'fixed', inset:0, background:'#000000cc', zIndex:600,
      display:'flex', justifyContent:'center', alignItems:'flex-start', overflowY:'auto', padding:'40px 20px' }}>
      <div style={{ background:C.bg, border:`1px solid ${C.border}`, width:'100%', maxWidth:680, height:'fit-content', minHeight:300 }}>
        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:C.mono, fontSize:10, color:C.accent, letterSpacing:2 }}>
              {editing === null ? 'DEPARTMENT MANAGER' : editing === 'new' ? 'CREATE NEW DEPARTMENT' : `EDIT: ${editing.name?.toUpperCase()}`}
            </div>
            {editing === null && (
              <div style={{ fontFamily:C.sans, fontSize:11, color:C.dim, marginTop:2 }}>
                Create and manage team departments with custom report categories
              </div>
            )}
          </div>
          {editing !== null && (
            <button onClick={() => setEditing(null)}
              style={{ background:'none', border:`1px solid ${C.border}`, color:C.dim,
                fontFamily:C.mono, fontSize:9, cursor:'pointer', padding:'5px 12px', letterSpacing:1 }}>
              ← BACK
            </button>
          )}
          <button onClick={onClose}
            style={{ background:'none', border:'none', color:C.dim, cursor:'pointer', fontSize:22, lineHeight:1 }}>×</button>
        </div>

        {/* List view */}
        {editing === null && (
          <div style={{ padding:'16px 20px' }}>
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
              <button onClick={() => setEditing('new')}
                style={{ padding:'8px 20px', background:C.accent, border:'none', color:'#000',
                  fontFamily:C.mono, fontSize:10, cursor:'pointer', fontWeight:700, letterSpacing:1 }}>
                + CREATE DEPARTMENT
              </button>
            </div>
            {loading && (
              <div style={{ textAlign:'center', padding:'32px 0', fontFamily:C.mono, fontSize:12, color:C.dim }}>Loading…</div>
            )}
            {!loading && depts.length === 0 && (
              <div style={{ textAlign:'center', padding:'32px 0', fontFamily:C.mono, fontSize:12, color:C.dim }}>
                No custom departments yet. Click + CREATE DEPARTMENT to add one.
              </div>
            )}
            {depts.map(dept => (
              <DeptListItem key={dept.id} dept={dept}
                onEdit={d => setEditing(d)}
                onDelete={deleteDept}
                onOpen={openDept} />
            ))}
          </div>
        )}

        {/* Form view */}
        {editing !== null && (
          <DeptForm
            existing={editing === 'new' ? null : editing}
            takenIds={depts.map(d => d.id)}
            userId={userId}
            userName={userName}
            onSave={() => setEditing(null)}
            onCancel={() => setEditing(null)} />
        )}
      </div>
    </div>
  );
}
